// Small badge shown in the Navbar while a user is mid-trial. Pulls the
// trial_end timestamp from useSubscription and renders "X days left" with
// a click-through to /pricing so they can decide whether to keep it.
//
// Returns null in every other state (no user, no trial, expired) so the
// Navbar layout stays clean for the 99% case.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const TrialBadge = () => {
  const { status } = useSubscription();

  const daysLeft = useMemo(() => {
    if (!status || status.stripeStatus !== "trialing" || !status.trial_end) return null;
    const ms = new Date(status.trial_end).getTime() - Date.now();
    if (ms <= 0) return null;
    // Round up so the last partial day still reads as "1 day left" instead
    // of "0 days left" — the trial really does still cover that day.
    return Math.max(1, Math.ceil(ms / MS_PER_DAY));
  }, [status]);

  if (daysLeft === null) return null;

  const urgent = daysLeft <= 2;

  return (
    <Link
      to="/account"
      className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono transition-colors ${
        urgent
          ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/15"
          : "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
      }`}
      aria-label={`Trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
      title={`Free trial — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
    >
      <Clock className="w-3 h-3" />
      <span>
        Trial: {daysLeft}d left
      </span>
    </Link>
  );
};

export default TrialBadge;
